import type { FilterChannel } from './channel-filter'

/**
 * Which board draws an order - `FRONTEND.md` §9.688, `BACKEND.md` §10A.347, owner #24 (O79).
 *
 * **The owner, through the operator**: *"samostatnú sekciu pre terasu ... Otvorene stoly OFYR a KUCHYNA OFYR a
 * tam mu bude ukazovať len objednávky z terasy ... aby vedel aj vypnúť a zapnúť"*, and #114: *"make it in Sales
 * channel configurable"*. A channel with `ownBoards` gets its own *Kuchyňa* and *Otvorené stoly*; the ordinary
 * ones stop drawing its orders; switched off, its orders are on the ordinary boards again.
 *
 * **One rule for both boards and the sidebar**, because three copies of *which orders go where* is how a
 * terrace order ends up on two boards or on none. Keyed on the channel, never on a zone: an order carries its
 * channel (`orders.type`) and does not carry its zone.
 *
 * **The failure direction is everything on the ordinary boards.** Channels not loaded, a slug nobody knows, a
 * channel switched off, the flag off - every one answers *no own boards*. This is the opposite of
 * `channelActive`'s fail-open, deliberately: that one keeps a feature from vanishing while a request is in
 * flight, and here vanishing is what a wrong *yes* would do to an order.
 */

/** The channels that run their own boards right now: switched on **and** ticked, read off loaded rows. */
export function ownBoardChannels(channels: readonly FilterChannel[]): FilterChannel[] {
  return channels.filter((channel) => channel.isActive && channel.ownBoards)
}

/**
 * The channel a board is for, while it still has its boards - or `null` for the ordinary board, and for a
 * channel that has lost them (flag off, switched off, never existed).
 */
export function boardChannelOf(
  boardChannel: string | null | undefined,
  channels: readonly FilterChannel[],
): FilterChannel | null {
  if (boardChannel === null || boardChannel === undefined || boardChannel === '') return null
  return ownBoardChannels(channels).find((channel) => channel.slug === boardChannel) ?? null
}

/**
 * Whether an order of this channel belongs on this board.
 *
 * `boardChannel` is `null` for the ordinary *Kuchyňa* / *Otvorené stoly*, which draw everything **except** the
 * orders of a channel with its own boards. A channel's own board draws that channel's orders only - and nothing
 * at all once the channel has lost its boards, because by then those orders are on the ordinary board and
 * drawing them twice would let two cooks start one order.
 */
export function onBoard(
  type: string | null | undefined,
  boardChannel: string | null | undefined,
  channels: readonly FilterChannel[],
): boolean {
  const slug = type ?? ''
  const own = ownBoardChannels(channels)
  if (boardChannel === null || boardChannel === undefined || boardChannel === '') {
    return !own.some((channel) => channel.slug === slug)
  }
  return slug === boardChannel && own.some((channel) => channel.slug === boardChannel)
}

/** The rows a board draws - {@link onBoard} over a list. */
export function boardOrders<T extends { type?: string | null }>(
  rows: readonly T[],
  boardChannel: string | null | undefined,
  channels: readonly FilterChannel[],
): T[] {
  return rows.filter((row) => onBoard(row.type, boardChannel, channels))
}

/**
 * For the ordinary board: each channel that has its own, with how many of these rows it holds.
 *
 * So a board narrowed by configuration says so - a cook looking at a quiet *Kuchyňa* while the terrace's is
 * full must be able to see that, which is the rule `selectionNote` applies to a narrowed filter.
 */
export function ownBoardCounts<T extends { type?: string | null }>(
  rows: readonly T[],
  channels: readonly FilterChannel[],
): { channel: FilterChannel; count: number }[] {
  return ownBoardChannels(channels).map((channel) => ({
    channel,
    count: rows.filter((row) => (row.type ?? '') === channel.slug).length,
  }))
}

/** The path of a channel's own kitchen - its own path, because the sidebar highlights by exact path. */
export function channelKitchenPath(slug: string): string {
  return `/kitchen/channel/${slug}`
}

/** The path of a channel's own open tables. */
export function channelRestaurantPath(slug: string): string {
  return `/restaurant/channel/${slug}`
}
